import { IonGrid, IonRow, IonCol, IonIcon, IonButton, IonButtons } from '@ionic/react';
import { ApexOptions } from 'apexcharts';
import { closeCircleOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
// eslint-disable-next-line import/default
import Chart from 'react-apexcharts';

import styles from '@/pages/ReportV2/components/Modals/ModalContent.module.css';

import { Loading } from '@/components/Loading/Loading';

import { uuid } from '@/utils';
import { mergeClassNames } from '@/utils/mergeClassNames';

import { GraphSeriesData, SingleSideVariabilityGraphAnnotations } from '@/models/reportV2.model';

type InterLimbCoordinationModalContentProps = {
  crp_Left: GraphSeriesData[];
  crp_Right: GraphSeriesData[];
  coordinativeVariabilityData_Left: GraphSeriesData[];
  coordinativeVariabilityData_Right: GraphSeriesData[];
  setShowMoreInfoModal: any;
  annotations?: boolean;
  graphAnnotations_Left?: SingleSideVariabilityGraphAnnotations[];
  graphAnnotations_Right?: SingleSideVariabilityGraphAnnotations[];
};

function createLegend(crpData: any): any[] {
  const crpLegend = crpData.map((entry: any) => {
    return {
      id: uuid(),
      name: entry.name,
      color: entry.color,
    };
  });

  const allStridesEntry = [
    {
      id: uuid(),
      name: 'All planes',
      color: '#4d4f5c',
    },
  ];

  return [...crpLegend, ...allStridesEntry];
}

export const InterLimbCoordinationModalContent = ({
  crp_Left,
  crp_Right,
  coordinativeVariabilityData_Left,
  coordinativeVariabilityData_Right,
  setShowMoreInfoModal,
  annotations,
  graphAnnotations_Left,
  graphAnnotations_Right,
}: InterLimbCoordinationModalContentProps) => {
  const [loading, setLoading] = useState(true);
  const [legend, setLegend] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('All planes');
  const [showCRPData_Left, setShowCRPData_Left] = useState<any[]>([]);
  const [showCRPData_Right, setShowCRPData_Right] = useState<any[]>([]);
  const [showCoordinativeVariabilityData_Left, setShowCoordinativeVariabilityData_Left] = useState<any[]>([]);
  const [showCoordinativeVariabilityData_Right, setShowCoordinativeVariabilityData_Right] = useState<any[]>([]);

  const fillOpacity: number[] = [];
  for (const value of Object.values(crp_Left)) {
    const Value = value as unknown as any;
    const name = Value.name;
    if (
      name === 'Calf (s) / Foot (s)' ||
      name === 'Thigh (s) / Calf (s)' ||
      name === 'Pelvis (s) / Thigh (s)' ||
      name === 'Thigh (s) / Thigh (s)'
    ) {
      fillOpacity.push(1);
    } else {
      fillOpacity.push(1);
    }
  }

  // Tab change set the active tab and the data for the graphs.
  const tabChange = (name: string) => {
    localStorage.setItem('activeTab', name);
    setActiveTab(name);
    if (name === 'All planes') {
      setShowCRPData_Left(crp_Left);
      setShowCRPData_Right(crp_Right);
      setShowCoordinativeVariabilityData_Left(coordinativeVariabilityData_Left);
      setShowCoordinativeVariabilityData_Right(coordinativeVariabilityData_Right);
    } else {
      setShowCRPData_Left([crp_Left.find((element: any) => element.name === name)]);
      setShowCRPData_Right([crp_Right.find((element: any) => element.name === name)]);
      setShowCoordinativeVariabilityData_Left([
        coordinativeVariabilityData_Left.find((element: any) => element.name === name),
      ]);
      setShowCoordinativeVariabilityData_Right([
        coordinativeVariabilityData_Right.find((element: any) => element.name === name),
      ]);
    }
  };

  useEffect(() => {
    const activeTab = localStorage.getItem('activeTab');
    console.log('activeTab', activeTab);
    if (activeTab) {
      setActiveTab(activeTab);
    } else {
      localStorage.setItem('activeTab', 'All planes');
      setActiveTab('All planes');
    }
  }, []);

  useEffect(() => {
    setLegend(createLegend(crp_Left));
    setShowCRPData_Left(crp_Left);
    setShowCRPData_Right(crp_Right);
    setShowCoordinativeVariabilityData_Left(coordinativeVariabilityData_Left);
    setShowCoordinativeVariabilityData_Right(coordinativeVariabilityData_Right);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, [crp_Left]);

  const options_Left: ApexOptions = {
    chart: {
      type: 'line',
      background: '#ffffff',
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    fill: {
      opacity: fillOpacity,
    },
    legend: {
      show: false,
    },
    xaxis: {
      type: 'numeric',
      labels: {
        show: false,
        style: {
          fontSize: '8px',
        },
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      min: -200,
      max: 200,
      tickAmount: 5,
      labels: {
        formatter: function (value: number) {
          return value.toFixed(0);
        },
        style: {
          fontSize: '8px',
        },
      },
    },
    subtitle: {
      text: 'Continouos Relative Phase',
      align: 'center',
      offsetY: 5,
      style: {
        fontSize: '8px',
        fontFamily: 'Montserrat',
      },
    },

    stroke: {
      width: [1],
      dashArray: [0],
    },
    tooltip: {
      enabled: false,
    },
    annotations: {
      xaxis: annotations ? graphAnnotations_Left : [],
      yaxis: [
        {
          y: 0,

          strokeDashArray: 5,
        },
      ],
    },
  };

  const optionsVariability_Left: ApexOptions = {
    chart: {
      background: '#ffffff',
      events: {
        legendClick: function (chartContext: any, seriesIndex: any, config: any) {
          if (import.meta.env.MODE !== 'production') console.log('legendClick', seriesIndex);
        },
      },
      type: 'line',
      zoom: {
        enabled: true,
      },
      toolbar: {
        show: false,
      },
    },
    legend: {
      show: false,
    },

    xaxis: {
      type: 'numeric',
      labels: {
        show: false,
        style: {
          fontSize: '8px',
        },
      },
      axisTicks: {
        show: false,
      },
    },
    fill: {
      opacity: 1,
    },
    yaxis: {
      min: 0,
      max: 360,
      tickAmount: 5,
      labels: {
        formatter: function (value: number) {
          const percentage = (value / 360) * 100;
          return percentage.toFixed(0) + ' %';
        },
        style: {
          fontSize: '8px',
        },
      },
    },
    subtitle: {
      text: 'Coordinative Variability',
      align: 'center',
      offsetY: 5,
      floating: true,
      style: {
        fontSize: '8px',
        fontFamily: 'Montserrat',
      },
    },

    stroke: {
      width: [1, 1],
      dashArray: [0, 4, 0],
    },
    tooltip: {
      enabled: false,
    },
    annotations: {
      xaxis: annotations ? graphAnnotations_Left : [],
    },
  };

  const options_Right: ApexOptions = {
    chart: {
      type: 'line',
      background: '#ffffff',
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    fill: {
      opacity: fillOpacity,
    },
    legend: {
      show: false,
    },
    xaxis: {
      type: 'numeric',
      labels: {
        show: false,
        style: {
          fontSize: '8px',
        },
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      min: -200,
      max: 200,
      tickAmount: 5,
      labels: {
        formatter: function (value: number) {
          return value.toFixed(0);
        },
        style: {
          fontSize: '8px',
        },
      },
    },
    subtitle: {
      text: 'Continouos Relative Phase',
      align: 'center',
      offsetY: 5,
      style: {
        fontSize: '8px',
        fontFamily: 'Montserrat',
      },
    },

    stroke: {
      width: [1],
      dashArray: [0],
    },
    tooltip: {
      enabled: false,
    },
    annotations: {
      xaxis: annotations ? graphAnnotations_Right : [],
      yaxis: [
        {
          y: 0,

          strokeDashArray: 5,
        },
      ],
    },
  };

  const optionsVariability_Right: ApexOptions = {
    chart: {
      background: '#ffffff',
      events: {
        legendClick: function (chartContext, seriesIndex, config) {
          if (import.meta.env.MODE !== 'production') console.log('legendClick', seriesIndex);
        },
      },
      type: 'line',
      zoom: {
        enabled: true,
      },
      toolbar: {
        show: false,
      },
    },
    legend: {
      show: false,
    },

    xaxis: {
      type: 'numeric',
      labels: {
        show: false,
        style: {
          fontSize: '8px',
        },
      },
      axisTicks: {
        show: false,
      },
    },
    fill: {
      opacity: 1,
    },
    yaxis: {
      min: 0,
      max: 360,
      tickAmount: 5,
      labels: {
        formatter: function (value: number) {
          const percentage = (value / 360) * 100;
          return percentage.toFixed(0) + ' %';
        },
        style: {
          fontSize: '8px',
        },
      },
    },
    subtitle: {
      text: 'Coordinative Variability',
      align: 'center',
      offsetY: 5,
      floating: true,
      style: {
        fontSize: '8px',
        fontFamily: 'Montserrat',
      },
    },

    stroke: {
      width: [1, 1],
      dashArray: [0, 4, 0],
    },
    tooltip: {
      enabled: false,
    },
    annotations: {
      xaxis: annotations ? graphAnnotations_Right : [],
    },
  };

  const cleanUp = () => {
    localStorage.setItem('activeTab', 'All planes');
    setShowMoreInfoModal({ show: false, graph: '' });
  };

  return (
    <>
      <IonGrid className={styles.Modalcontent_container} fixed>
        <IonRow className={styles.variabilityLegend_Row}>
          <IonCol className='' size='11'>
            <div className={styles.variabilityLegend}>
              {legend.map((element, _) => {
                const color = element.color;
                const iconStyle = {
                  backgroundColor: color,
                };

                return (
                  <div
                    className={
                      activeTab === element.name
                        ? styles.variabilityLegend_Entry_Selected
                        : styles.variabilityLegend_Entry
                    }
                    key={element.id}
                    onClick={() => {
                      tabChange(element.name);
                    }}>
                    <div className={styles.legend_dot} style={iconStyle} />
                    <p className={styles.legend_name}>{element.name}</p>
                  </div>
                );
              })}
            </div>
          </IonCol>
          <IonCol className='ion-align-items-center' size='1'>
            <IonButtons className='ion-justify-content-center ion-align-self-center' style={{ height: '100%' }}>
              <IonButton className={styles.closeButton} fill='clear' onClick={() => cleanUp()} slot='icon-only'>
                <IonIcon icon={closeCircleOutline} />
              </IonButton>
            </IonButtons>
          </IonCol>
        </IonRow>
        <IonRow className='ion-align-items-center ion-align-items-stretch ion-padding-vertical ion-margin-vertical ion-justify-content-center'>
          {loading && <Loading />}
          {!loading && (
            <>
              <IonCol className=' ' size='6'>
                <IonRow className={styles.variabilityModalGraphRow}>
                  <IonCol className='ion-no-padding' size='12'>
                    <div className={styles.variabilityJointGraphModal_Wrapper}>
                      <Chart
                        height={'100%'}
                        options={options_Left}
                        series={showCRPData_Left}
                        type='line'
                        width={'100%'}
                      />
                    </div>
                  </IonCol>
                </IonRow>
                <IonRow className={mergeClassNames('ion-no-padding', styles.variabilityModalGraphRow)}>
                  <IonCol className='ion-no-padding' size='12'>
                    <div className={styles.variabilityJointGraphModal_Wrapper}>
                      <Chart
                        height={'100%'}
                        options={optionsVariability_Left}
                        series={showCoordinativeVariabilityData_Left}
                        type='line'
                        width={'100%'}
                      />
                    </div>
                  </IonCol>
                </IonRow>
                {/* <FunctionalMovementVariabilityJointGraph
      crpData={crp_Left}
      coordinativeVariabilityData={coordinativeVariabilityData_Left}
    /> */}
              </IonCol>
              <IonCol className='ion-no-padding' size='6'>
                <IonRow className={styles.variabilityModalGraphRow}>
                  <IonCol className='ion-no-padding' size='12'>
                    <div className={styles.variabilityJointGraphModal_Wrapper}>
                      <Chart
                        height={'100%'}
                        options={options_Right}
                        series={showCRPData_Right}
                        type='line'
                        width={'100%'}
                      />
                    </div>
                  </IonCol>
                </IonRow>
                <IonRow className={mergeClassNames('ion-no-padding', styles.variabilityModalGraphRow)}>
                  <IonCol className='ion-no-padding' size='12'>
                    <div className={styles.variabilityJointGraphModal_Wrapper}>
                      <Chart
                        height={'100%'}
                        options={optionsVariability_Right}
                        series={showCoordinativeVariabilityData_Right}
                        type='line'
                        width={'100%'}
                      />
                    </div>
                  </IonCol>
                </IonRow>
                {/* <FunctionalMovementVariabilityJointGraph
      crpData={crp_Right}
      coordinativeVariabilityData={coordinativeVariabilityData_Right}
    /> */}
              </IonCol>
            </>
          )}
        </IonRow>
      </IonGrid>
      {/* <IonButton slot='icon-only' onClick={() => cleanUp()} fill='clear' className='ORYX_ModalButton_Close'>
        <IonIcon icon={closeCircleOutline} />
      </IonButton> */}
    </>
  );
};
